<template>
    <div>
        <div class="tv-base-packages-mobile-select-wrapper lg:hidden">
            <select-input
                v-model="selectedPackageId"
                name="packages"
                :options="packagesAsOptions"
                :placeholder="lang('Välj baspaket','Select base package')"
                @input="changePackage"
            />
        </div>
        <div class="justify-between mb-2xl -mx-lg hidden lg:flex">
            <div
                v-for="tvPackage in packages"
                :key="tvPackage.id"
                class="w-full mx-lg"
            >
                <a
                    class="product-tab h-full  relative group"
                    :class="{ 'is-active': activePackage === tvPackage, 'is-campaign': tvPackage.has_campaign }"
                    @click="togglePackage(tvPackage)"
                >
                    <div v-if="tvPackage.has_campaign" class="campaign">
                        <span class="campaign-badge mx-auto">
                            {{ lang('Kampanj','Campaign') }}
                        </span>
                    </div>
                    <tv-base-package-summary
                        :tv-package="tvPackage"
                    />
                    <div>
                        <show-hide-button
                            :active="activePackage === tvPackage"
                            button-classes="btn btn-md is-primary lg:mt-xl"
                        />
                    </div>
                </a>
            </div>
        </div>
        <div id="active-base-package" />
        <div
            v-if="activePackage"
            class="card relative hidden lg:block"
        >
            <div class="lg:hidden mb-xl">
                <tv-base-package-summary
                    :tv-package="activePackage"
                />
            </div>

            <tv-channel-list
                :tv-package="activePackage"
                header-class="h3"
            />

            <div class="md:flex justify-between md:mt-3xl">
                <slot name="depending-on-product" />

                <div class="w-full md:w-auto ml-auto">
                    <button
                        class="btn btn-lg is-primary"
                        :disabled="hasTvBasePackage || cannotDeliver || (! $isAnonymous && ! activePackage.can_be_added_to_cart)"
                        @click="primaryAction"
                    >
                        {{ btnOrderText }}
                    </button>

                    <div
                        v-if="! $customerIsLoggedIn"
                        class="mt-lg text-center text-sm"
                    >
                        {{ lang('Redan kund?', 'Already a customer?') }}
                        <button
                            type="button"
                            class="btn-link font-semibold text-sm"
                            @click="$modal.show('login-modal', { redirectBack: true })"
                        >
                            {{ btnLoginText }}
                        </button>
                    </div>
                </div>
            </div>
            <div
                v-if="hasTvBasePackage"
                class="bg-neutral-lighter rounded-lg mt-xl py-md px-lg flex items-center justify-start"
            >
                <font-awesome-icon icon="lightbulb" />
                <div class="text-sm ml-xl font-semibold">
                    {{ lang(
                        'Du har redan ett baspaket och kan därför inte beställa ett nytt baspaket här. Gå in på',
                        'You already have a base package, therefore you cannot order a new base package. Visit'
                    ) }}
                    <a :href="route('front.my-pages.index')">{{ lang('Mina sidor','My pages') }}</a>
                    {{ lang('och byt paket därifrån istället.','and change base packages from there') }}
                </div>
            </div>
        </div>
        <div
            v-if="activePackage"
            class="product-tab relative lg:hidden"
            :class="{ 'is-campaign': activePackage.has_campaign }"
        >
            <div
                v-if="activePackage.has_campaign"
                class="campaign"
            >
                <span class="campaign-badge mx-auto">
                    {{ lang('Kampanj', 'Campaign') }}
                </span>
            </div>

            <div class="lg:hidden mb-xl">
                <tv-base-package-summary
                    :tv-package="activePackage"
                />
            </div>

            <tv-channel-list
                :tv-package="activePackage"
                header-class="h3"
            />

            <div class="md:flex justify-between md:mt-3xl">
                <slot name="depending-on-product" />

                <div class="w-full md:w-auto ml-auto">
                    <button
                        class="btn btn-lg is-primary"
                        :disabled="hasTvBasePackage || cannotDeliver || (! $isAnonymous && ! activePackage.can_be_added_to_cart)"
                        @click="primaryAction"
                    >
                        {{ btnOrderText }}
                    </button>

                    <div
                        v-if="! $customerIsLoggedIn"
                        class="mt-lg text-center text-sm"
                    >
                        {{ lang('Redan kund?', 'Already a customer?') }}
                        <button
                            type="button"
                            class="btn-link font-semibold text-sm"
                            @click="$modal.show('login-modal', { redirectBack: true })"
                        >
                            {{ btnLoginText }}
                        </button>
                    </div>
                </div>
            </div>
            <div
                v-if="hasTvBasePackage"
                class="bg-neutral-lighter rounded-lg mt-xl py-md px-lg flex items-center justify-start"
            >
                <font-awesome-icon icon="lightbulb" />
                <div class="text-sm ml-xl font-semibold">
                    {{ lang(
                        'Du har redan ett baspaket och kan därför inte beställa ett nytt baspaket här. Gå in på',
                        'You already have a base package, therefore you cannot order a new base package. Visit'
                    ) }}
                    <a :href="route('front.my-pages.index')">{{ lang('Mina sidor','My pages') }}</a>
                    {{ lang('och byt paket därifrån istället.','and change base packages from there') }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import TvBasePackageSummary from './Partials/TvBasePackageSummary';
    import TvChannelList from './Partials/TvChannelList';

    export default {
        components: {
            TvBasePackageSummary,
            TvChannelList,
        },
        props: {
            packages: {
                type: Array,
                required: true,
            },
            packagesAsOptions: {
                type: Array,
                required: true,
            },
            hasTvBasePackage: {
                type: Boolean,
                default: false,
            },
            cannotDeliver: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                activePackage: null,
                selectedPackageId: this.activePackage ? this.activePackage.id : '',
                scroll: true,
            };
        },
        computed: {
            btnLoginText() {
                return this.translation('services_blocks_btn_login');
            },
            btnOrderText() {
                if (! this.$isAnonymous) {
                    return this.translation('services_blocks_btn_order_not_anonymous');
                }

                return this.translation('services_blocks_btn_order_anonymous');
            },
        },
        mounted() {
            // Select "Ownit TV Stor" as default if it exists
            let value = this.packages.find(tvPackage => tvPackage.name === 'Ownit TV Stor');
            if (! value) {
                value = this.packages[0];
            }

            this.scroll = false;
            this.changePackage(value.id);
            this.selectedPackageId = value.id;
            this.scroll = true;
        },
        methods: {
            primaryAction() {
                const url = window.route('front.products.tv-packages.show', this.activePackage.slug);
                if (this.activePackage.can_be_added_to_cart) {
                    window.location = url;
                    return;
                }

                // We need to emit an event to the address component that the user clicked
                // on this specific variant so we can redirect user after search
                EventBus.$emit('redirectAfterSearch', url);

                this.scrollItAndFocus('#address-search');
            },
            changePackage(value) {
                this.activePackage = this.packages.find(tvPackage => tvPackage.id === value);
                this.scrollToActivePackage();
            },
            togglePackage(tvPackage) {
                if (this.activePackage === tvPackage) {
                    this.activePackage = null;
                    return;
                }

                this.activePackage = tvPackage;
                this.scrollToActivePackage();
            },
            scrollToActivePackage() {
                if (! this.scroll) {
                    return;
                }

                const offset = window.utils.breakpoint('md') ? -352 : -104;
                window.utils.scrollIt('#active-base-package', 'smooth', null, offset);
            },
        },
    };
</script>
<style>
.tv-base-packages-mobile-select-wrapper {
    box-shadow: 0px 0px 0px 1px #0667ff;
}
</style>
